import { getSysConfigApi } from '@/api/basicService/systemSetting/systemConfig'
import { getFileListApi } from '@/api/publicAxios/publicAxios'
import { p_PreviewFile } from '@/utils/publicConfig'
import { I_PickLatestBySubKind } from '@/utils/publicImg'
import { useAppStoreWithOut } from '@/store/modules/app'

const DEFAULT_LOGO = '/logo-B5L-Cd3D.png'
const BRAND_INIT_TIMEOUT_MS = 5000

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => {
      setTimeout(() => resolve(undefined), ms)
    })
  ])
}

/** 更新浏览器 favicon */
export function setBrandFavicon(url: string) {
  const link =
    (document.getElementById('app-favicon') as HTMLLinkElement | null) ||
    (document.querySelector('link[rel="icon"]') as HTMLLinkElement | null)
  if (link) {
    link.href = url
  }
}

function setLoadingLogo(url: string) {
  const img = document.getElementById('app-loading-logo') as HTMLImageElement | null
  if (img) {
    img.src = url
  }
}

function setLoadingTitle(name: string) {
  const titleEl = document.getElementById('app-loading-title')
  if (titleEl) {
    titleEl.textContent = name
  }
}

/** 从系统配置拉取名称与 Logo，写入 store 并更新 favicon */
export async function loadSystemBrand(force = false) {
  const appStore = useAppStoreWithOut()
  if (appStore.brandLoaded && !force) {
    return
  }

  try {
    const res = await withTimeout(getSysConfigApi(), BRAND_INIT_TIMEOUT_MS)
    const data = res?.data
    if (!data) {
      appStore.markBrandLoaded()
      return
    }

    if (data.name) {
      appStore.setTitle(data.name)
      setLoadingTitle(data.name)
    }

    if (!data.bizFileId) {
      appStore.markBrandLoaded()
      return
    }

    const imgList = await withTimeout(getFileListApi(data.bizFileId, 'sysConfig'), BRAND_INIT_TIMEOUT_MS)
    if (!imgList || imgList?.code !== 0 || !Array.isArray(imgList.data)) {
      appStore.markBrandLoaded()
      return
    }

    // 同 subKind 可能残留多条未删附件，必须取最新，否则侧栏会回退到历史 Logo
    const iconFile = I_PickLatestBySubKind(imgList.data, 'sysConfigIcon')
    if (!iconFile?.fileId) {
      appStore.markBrandLoaded()
      return
    }

    const logoUrl = await withTimeout(p_PreviewFile(iconFile.fileId), BRAND_INIT_TIMEOUT_MS)
    if (!logoUrl) {
      appStore.markBrandLoaded()
      return
    }

    appStore.setBrandLogoUrl(logoUrl)
    setBrandFavicon(logoUrl)
    setLoadingLogo(logoUrl)
    appStore.markBrandLoaded()
  } catch {
    setBrandFavicon(DEFAULT_LOGO)
    setLoadingLogo(DEFAULT_LOGO)
    appStore.markBrandLoaded()
  }
}

/** 启动页品牌资源：与侧边栏 Logo、窗口标题同源（系统配置） */
export async function initAppBrand() {
  await loadSystemBrand()
}

/** 系统配置保存后强制刷新品牌信息 */
export async function refreshSystemBrand() {
  await loadSystemBrand(true)
}
